/**
 * Lightweight in-client profiler — Phase A1.
 *
 * Why not Chrome devtools / puppeteer-tracing? Synthetic traces miss
 * real-world variance (router latency, browser background tabs, GC under
 * memory pressure). This profiler runs on real visitors at low overhead
 * and exposes a console-pasteable dump.
 *
 * Public surface:
 *   profiler.mark('frame.start')                     // event timestamp
 *   profiler.measure('mesher.insertChunk', startMs)  // duration sample
 *   profiler.tick(deltaMs)                            // frame-time sample
 *   profiler.dump()                                   // returns aggregated stats
 *
 * Each name keeps a ring buffer of 600 samples. Stats are computed lazily
 * in dump() so the hot path is just `arr[i++ % N] = value`.
 *
 * Enable via `?perf=1` or `localStorage.perfProfiler = '1'` to avoid the
 * overhead on regular sessions.
 */

const BUFFER_SIZE = 600

type Buffer = {
  samples: Float32Array
  idx: number
  count: number
}

const buffers: Record<string, Buffer> = {}

const enabled = (() => {
  try {
    if (typeof location !== 'undefined' && new URLSearchParams(location.search).get('perf') === '1') return true
    if (typeof localStorage !== 'undefined' && localStorage.perfProfiler === '1') return true
  } catch { /* ssr */ }
  return false
})()

const getBuffer = (name: string): Buffer => {
  let b = buffers[name]
  if (!b) {
    b = { samples: new Float32Array(BUFFER_SIZE), idx: 0, count: 0 }
    buffers[name] = b
  }
  return b
}

const pushSample = (name: string, value: number) => {
  if (!enabled) return
  const b = getBuffer(name)
  b.samples[b.idx] = value
  b.idx = (b.idx + 1) % BUFFER_SIZE
  if (b.count < BUFFER_SIZE) b.count++
}

const percentile = (sorted: number[], p: number): number => {
  if (sorted.length === 0) return 0
  const idx = Math.max(0, Math.min(sorted.length - 1, Math.floor(sorted.length * p)))
  return sorted[idx]
}

type Stats = { count: number; p50: number; p95: number; p99: number; max: number; mean: number }

const statsFor = (b: Buffer): Stats => {
  const used: number[] = []
  for (let i = 0; i < b.count; i++) used.push(b.samples[i])
  used.sort((a, b) => a - b)
  let sum = 0
  for (const v of used) sum += v
  return {
    count: used.length,
    p50: percentile(used, 0.50),
    p95: percentile(used, 0.95),
    p99: percentile(used, 0.99),
    max: used.length > 0 ? used[used.length - 1] : 0,
    mean: used.length > 0 ? sum / used.length : 0,
  }
}

// One-shot milestones (e.g. time-to-first-chunk) — distinct from rolling
// samples. Each milestone records ms since page load.
const milestones: Record<string, number> = {}
const pageLoadT0 = typeof performance !== 'undefined' ? performance.now() : 0

export const profiler = {
  enabled,
  /** Record a milestone (e.g. firstChunk, firstFrame) — ms since page load. */
  recordMilestone (name: string) {
    if (milestones[name] !== undefined) return
    milestones[name] = performance.now() - pageLoadT0
  },
  /** Get the milestone snapshot. */
  getMilestones (): Record<string, number> {
    return { ...milestones }
  },
  /** Record `now - since` as a sample under `name`. Returns the duration. */
  measure (name: string, since: number): number {
    const dur = performance.now() - since
    pushSample(name, dur)
    return dur
  },
  /** Push a pre-computed sample. */
  sample (name: string, value: number) {
    pushSample(name, value)
  },
  /** Convenience: per-frame deltaMs. */
  tick (deltaMs: number) {
    pushSample('frame.time', deltaMs)
  },
  /** Aggregate all buffers; returns p50/p95/p99/max/mean per metric. */
  dump (): Record<string, Stats> {
    const out: Record<string, Stats> = {}
    for (const k of Object.keys(buffers)) out[k] = statsFor(buffers[k])
    return out
  },
  /** Reset all buffers. */
  reset () {
    for (const k of Object.keys(buffers)) {
      buffers[k].idx = 0
      buffers[k].count = 0
    }
  },
}

if (typeof window !== 'undefined') {
  ;(window as any).__perfDump = () => profiler.dump()
  ;(window as any).__perfReset = () => profiler.reset()
  if (enabled) {
    console.log('[perf] profiler enabled — call __perfDump() in console for stats')
  }
}
